// Wire protocol shared by client and Worker. Keep small + versioned.
export const PROTOCOL_VERSION = 1;

export type PlaneSkin = 0 | 1 | 2 | 3;

export interface InputState {
  // -1..1 axes
  pitch: number;
  yaw: number;
  roll: number;
  throttle: number; // 0..1
  fire: boolean;
  skill: 0 | 1 | 2 | 3; // 0 = none, 1..3 = skill index just-pressed
  seq: number; // monotonic input sequence for ack
  // When true (and pitch/roll near 0), the plane gradually returns to level
  // flight on its own. War-Thunder-style stabilizer.
  autoLevel: boolean;
  // World-space aim direction the player is currently looking at (cursor
  // raycasted through the camera). Server validates it falls in the plane's
  // forward cone and uses it as the laser fire direction. Optional — defaults
  // to plane forward if absent or invalid.
  aimX?: number; aimY?: number; aimZ?: number;
}

export interface PlayerSnapshot {
  id: string;
  name: string;
  skin: PlaneSkin;
  x: number; y: number; z: number;
  qx: number; qy: number; qz: number; qw: number;
  vx: number; vy: number; vz: number;
  hp: number;
  alive: boolean;
  kills: number;
  deaths: number;
  dashCd: number;
  dashT: number;
  missileCd: number;
  shieldCd: number;
  shieldT: number;
  // Laser ("Xerath ult"): when laserChargeT > 0, the plane is channeling.
  // laserCd is post-fire cooldown (0 when ready).
  laserChargeT: number;
  laserChargeMax: number;
  laserCd: number;
  // Engine power: 0..1 (lags throttle by engineSpool seconds).
  engineRpm: number;
  // Commanded throttle 0..1.
  throttle: number;
  // Last known cursor-aim direction (world unit vector). Used by the laser
  // telegraph so other players see where the channel is pointing.
  aimX: number; aimY: number; aimZ: number;
}

export interface BulletSnapshot {
  id: number;
  ownerId: string;
  x: number; y: number; z: number;
  vx: number; vy: number; vz: number;
  ttl: number;
  kind: 0 | 1; // 0 bullet, 1 missile
}

export interface TargetSnapshot {
  id: number;
  x: number; y: number; z: number;
  r: number;       // visual + collision radius
  alive: boolean;  // false → hidden client-side, awaiting respawn
}

// Kill ring: a glowing ring dropped at the kill location. Players can press
// a "boost to last kill" key to receive a smooth turbo toward the ring.
export interface KillRingSnapshot {
  id: number;
  x: number; y: number; z: number;
  ttl: number;     // seconds remaining before the ring fades away
}

export interface ExplosionEvent {
  x: number; y: number; z: number; r: number;
}

export interface LaserFireEvent {
  ownerId: string;
  ox: number; oy: number; oz: number;
  dx: number; dy: number; dz: number;
  length: number;       // visible beam length (capped by hit or range)
  hitId: string | "";   // empty if missed
  blocked: boolean;     // true if hit a shielded plane
}

export type MapType = "skybreak" | "alps" | "atoll" | "outback" | "metropolis";

export interface MapDef {
  id: MapType;
  name: string;
  desc: string;
}

export const MAPS: Record<MapType, MapDef> = {
  skybreak:   { id: "skybreak",   name: "Skybreak",   desc: "Mixed terrain" },
  alps:       { id: "alps",       name: "Alpine",     desc: "High peaks · snow" },
  atoll:      { id: "atoll",      name: "Atoll",      desc: "Open sea · islands" },
  outback:    { id: "outback",    name: "Outback",    desc: "Open plains" },
  metropolis: { id: "metropolis", name: "Megalopolis", desc: "Skyscraper canyons" },
};

// Per-map world-generation parameters. Shared verbatim by client renderer
// and server collision generator so they always agree.
export interface MapParams {
  heightAmp: number;       // multiplier on terrain height
  seaLevel: number;        // override TERRAIN.seaLevel
  noiseScaleHi: number;    // higher freq detail
  domainWarp: number;      // domain warp amplitude
  buildingDensity: number; // 0..1 — fraction of grid cells that get buildings
  buildingHeightMin: number;
  buildingHeightMax: number;
  buildingGridStep: number;
  flatten: number;         // 0..1 — pulls heights toward sea level
  treeDensity: number;     // 0..1
  collidableBuildings: boolean; // server-side bbox kill
}

export const MAP_PARAMS: Record<MapType, MapParams> = {
  skybreak:   { heightAmp: 1.0, seaLevel: 4,  noiseScaleHi: 1.0,  domainWarp: 80, buildingDensity: 0.18, buildingHeightMin: 4, buildingHeightMax: 10, buildingGridStep: 36, flatten: 0,    treeDensity: 0.25, collidableBuildings: false },
  alps:       { heightAmp: 1.45, seaLevel: 2, noiseScaleHi: 1.2,  domainWarp: 60, buildingDensity: 0.05, buildingHeightMin: 4, buildingHeightMax: 8,  buildingGridStep: 60, flatten: 0,    treeDensity: 0.30, collidableBuildings: false },
  atoll:      { heightAmp: 0.55, seaLevel: 8, noiseScaleHi: 0.85, domainWarp: 100, buildingDensity: 0.04, buildingHeightMin: 3, buildingHeightMax: 6,  buildingGridStep: 70, flatten: 0.25, treeDensity: 0.18, collidableBuildings: false },
  outback:    { heightAmp: 0.45, seaLevel: 0, noiseScaleHi: 0.6,  domainWarp: 40, buildingDensity: 0.06, buildingHeightMin: 3, buildingHeightMax: 7,  buildingGridStep: 50, flatten: 0.55, treeDensity: 0.10, collidableBuildings: false },
  metropolis: { heightAmp: 0.10, seaLevel: -3, noiseScaleHi: 0.4, domainWarp: 0,  buildingDensity: 0.78, buildingHeightMin: 28, buildingHeightMax: 90, buildingGridStep: 28, flatten: 0.95, treeDensity: 0.0,  collidableBuildings: true  },
};

export type C2S =
  | { t: "join"; name: string; skin: PlaneSkin; arena: string }
  | { t: "input"; i: InputState }
  | { t: "chat"; text: string }
  | { t: "mute"; target: string; on: boolean }
  | { t: "ping"; ts: number }
  | { t: "respawn" }
  | { t: "boostToRing" };

export type S2C =
  | { t: "welcome"; id: string; seed: number; arena: string; mapType: MapType; tick: number }
  | { t: "state"; tick: number; players: PlayerSnapshot[]; bullets: BulletSnapshot[]; targets: TargetSnapshot[]; rings: KillRingSnapshot[]; ackSeq: number }
  | { t: "events"; explosions: ExplosionEvent[]; kills: { killer: string; victim: string }[]; lasers: LaserFireEvent[] }
  | { t: "chat"; from: string; name: string; text: string; system?: boolean }
  | { t: "pong"; ts: number; serverTs: number }
  | { t: "error"; msg: string };

export const TERRAIN = {
  size: 4096,           // world spans -size/2 .. size/2 on X/Z
  segments: 256,        // heightmap resolution
  seaLevel: 4,
  maxHeight: 220,
  worldBoundsY: [10, 800] as const,
};

export const SKILLS = {
  // Dash: brief afterburner — adds raw thrust + lifts the speed cap so the
  // plane can blow past its normal max for a moment. Bigger boost makes the
  // dash visibly punchy.
  dash:    { cooldown: 6,  duration: 1.5, boost: 90 },
  missile: { cooldown: 9,  count: 3,      speed: 200 },
  shield:  { cooldown: 12, duration: 3 },
};

export const LASER = {
  charge: 1.2,
  cooldown: 1.8,
  range: 800,
  radius: 4.5,
  damage: 200,
};

export const PLANE = {
  // Top speed at full throttle. Determined by thrust vs drag equilibrium.
  maxSpeed: 80,
  // Peak thrust (m/s²) when engineRpm is 1.0.
  baseThrust: 40,
  // Drag coefficients (40 ≈ 0.40 * 80 + 0.0015 * 80²  →  cruise ≈ 78).
  dragLinear:    0.40,
  dragQuadratic: 0.0015,
  // First-order time constant (s) for engineRpm → commanded throttle.
  engineSpool:   0.60,
  // Below stallSpeed → no lift. Linear ramp to liftReady where lift is full.
  stallSpeed: 14,
  liftReady:  32,
  liftCoef:   1.20,
  hp: 100,
  fireCooldown: 0.12,
  bulletSpeed: 220,
  bulletDamage: 8,
  missileDamage: 55,
  collisionRadius: 4.5,
};
